/*
  Highlights the link for the section currently in view.
*/
const ACTIVE_OFFSET = 0.25;

const tablesOfContents = () =>
  <NodeListOf<HTMLElement>>document.querySelectorAll(".js-table-of-contents");

const initTableOfContents = (tableOfContents: HTMLElement) => {
  const links = Array.from(
    <NodeListOf<HTMLAnchorElement>>(
      tableOfContents.querySelectorAll(".js-table-of-contents-link")
    )
  );

  const targets = links
    .map((link) => ({
      link,
      section: document.getElementById(link.hash.slice(1)),
    }))
    .filter(
      (target): target is { link: HTMLAnchorElement; section: HTMLElement } =>
        target.section instanceof HTMLElement
    );

  if (!targets.length) {
    return;
  }

  const currentLabel = tableOfContents.querySelector(
    ".js-table-of-contents-current"
  );
  const select = tableOfContents.querySelector(".js-table-of-contents-select");

  const setActive = (activeLink: HTMLAnchorElement) => {
    targets.forEach(({ link }) => {
      const isActive = link === activeLink;

      if (isActive && currentLabel) {
        currentLabel.textContent = link.textContent?.trim() ?? "";
      }

      if (isActive && select instanceof HTMLSelectElement) {
        select.value = link.hash.slice(1);
      }

      link.classList.toggle("is-active", isActive);

      if (isActive) {
        link.setAttribute("aria-current", "location");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  };

  /*
    A clicked link stays active until the user scrolls themselves.
    Fixes issue when TOC is at the bottom of a page.
  */
  let clickedLink: HTMLAnchorElement | null = null;

  targets.forEach(({ link }) => {
    link.addEventListener("click", () => {
      clickedLink = link;
      setActive(link);
    });
  });

  if (select instanceof HTMLSelectElement) {
    select.addEventListener("change", () => {
      const target = targets.find(
        ({ link }) => link.hash.slice(1) === select.value
      );

      if (!target) {
        return;
      }

      clickedLink = target.link;
      setActive(target.link);
      target.section.scrollIntoView();
      history.replaceState(null, "", target.link.hash);
    });
  }

  const releaseClickedLink = () => {
    clickedLink = null;
  };

  ["wheel", "touchstart", "pointerdown", "keydown"].forEach((eventName) =>
    window.addEventListener(eventName, releaseClickedLink, { passive: true })
  );

  const update = () => {
    if (clickedLink) {
      return;
    }

    const threshold = window.innerHeight * ACTIVE_OFFSET;
    const isAtBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;

    if (isAtBottom) {
      setActive(targets[targets.length - 1].link);
      return;
    }

    const current = targets
      .filter(({ section }) => section.getBoundingClientRect().top <= threshold)
      .pop();

    setActive((current ?? targets[0]).link);
  };

  let ticking = false;

  const onScroll = () => {
    if (ticking) {
      return;
    }

    ticking = true;

    window.requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  };

  update();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
};

tablesOfContents().forEach(initTableOfContents);
